import { GetServerSideProps } from 'next'
import dynamic from 'next/dynamic'
import React, { useRef } from 'react'

import { NextSeo } from 'next-seo'
import { convertNodeToElement } from 'react-html-parser'

import { Box, Grid, NoSsr } from '@material-ui/core'
import { createStyles, makeStyles } from '@material-ui/core/styles'

import AppLayout from '~/components/layouts/AppLayout'
import AdsSlot from '~/components/shared/AdSlot'
import HeroSection from '~/components/shared/HeroSection'
import ReactHtmlParser from '~/components/shared/ReactHtmlParser'
import Section from '~/components/shared/Section'
import Text, { Title } from '~/components/shared/Text'
import { ReCaptchaProvider } from '~/containers/ReCaptchaProvider'
import ArticleListings from '~/views/Blog/ArticleListings'
import BlogCards from '~/views/Blog/BlogCards'
import BlogHeader from '~/views/Blog/BlogHeader'

import EmpriseGroup from '~/services/EmpriseGroup'
import DFPSlotsProvider from '~/utils/DFPSlotsProvider'

import { Theme } from '~/theme/theme'

import { BlogArticle } from '~/models/Blog'
import { SlugModel } from '~/models/Slug'

const ArticleImageGallery = dynamic(
  () => import('~/views/Blog/articleSlider/ArticleImageGallery'),
  {
    ssr: false
  }
)

const SubscribeSection = dynamic(
  () => import('~/components/sections/SubscribeSection'),
  { ssr: false }
)

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    [theme.breakpoints.down('xs')]: {
      ads: {
        justifyContent: 'center'
      }
    }
  })
)

export const BlogCategory = ({ data }: { data: BlogArticle }) => {
  const isMount = useRef(false)
  const classes = useStyles()

  const images = data.images?.map((image) => ({
    thumbnail: image.url,
    original: image.url,
    originalAlt: image.image_alt,
    thumbnailAlt: image.image_alt
  }))

  const adOptions = {
    pageType: 'article',
    pageKey: 'category',
    adUnit: 'news/article',
    targetingArguments: {
      tags: data.keywords || []
    }
  }

  return (
    <DFPSlotsProvider {...adOptions}>
      <AdsSlot
        renderOutOfThePage
        slotId="gam_oop_pos0"
        sizes={[[1, 1]]}
        targetingArguments={{ pos: '0' }}
        disableSizeMaping
      />
      <AppLayout
        disableGutters
        containerProps={{ disableGutters: true, maxWidth: false }}
      >
        <NextSeo
          title={data.meta_title}
          description={data.meta_description}
          openGraph={{
            title: data.meta_title,
            description: data.meta_description
          }}
        />
        <BlogHeader />
        <HeroSection
          darkness
          src={data.primary_image_url || images[0]?.original}
          placeholderImage="empty"
          height="350px"
          alt={data.primary_image_alt}
          sizes="(max-width: 600px) 200px, 1920px"
        >
          <Title
            m="auto"
            variant="h1"
            textAlign="center"
            style={{ color: 'white' }}
          >
            {data.page_title}
          </Title>
        </HeroSection>
        <Section bgcolor="common.white" title="">
          <Grid container spacing={3}>
            <Grid xs={12} md={8} lg={8} item>
              <Box maxWidth="780px">
                <Text fontSize={30} fontWeight={600} mb={3}>
                  {data.sub_heading}
                </Text>
                <ReactHtmlParser
                  data={data.content_body_web}
                  options={{
                    transform: (node, index, transform) => {
                      if (images.length && index === 2 && !isMount.current) {
                        isMount.current = true
                        return (
                          <div>
                            {convertNodeToElement(node, index, transform)}
                            <ArticleImageGallery items={images} />
                            <Box
                              display="flex"
                              flexWrap="wrap"
                              mt={2}
                              className={classes.ads}
                            >
                              <AdsSlot
                                slotId="gam_banner_pos1"
                                sizes={[[728, 90]]}
                                sizeMapping={[
                                  {
                                    viewport: [1280, 0],
                                    sizes: [[728, 90], [560, 315], 'fluid']
                                  },
                                  {
                                    viewport: [769, 0],
                                    sizes: [[300, 250], [560, 315], 'fluid']
                                  },
                                  {
                                    viewport: [0, 0],
                                    sizes: [
                                      [300, 250],
                                      [320, 180],
                                      [320, 100],
                                      [320, 50],
                                      [300, 50],
                                      'fluid'
                                    ]
                                  }
                                ]}
                                targetingArguments={{
                                  pos: '1',
                                  native: 'inarticle'
                                }}
                              />
                            </Box>
                          </div>
                        )
                      }
                    }
                  }}
                />
              </Box>
            </Grid>
            <Grid
              xs={12}
              sm={12}
              md={4}
              lg={4}
              item
              style={{ minWidth: '320px' }}
            >
              <NoSsr>
                <BlogCards data={data} />
              </NoSsr>
            </Grid>

            <Grid xs={12} item>
              <ArticleListings keywords={data?.keywords.join(', ')} />
            </Grid>
          </Grid>
        </Section>

        <ReCaptchaProvider>
          <SubscribeSection />
        </ReCaptchaProvider>

        <NoSsr>
          <Box display="flex" justifyContent="center" flexWrap="wrap" my={2}>
            <AdsSlot
              slotId="gam_footer_pos2"
              sizes={[[728, 90]]}
              sizeMapping={[
                {
                  viewport: [1280, 0],
                  sizes: [
                    [970, 90],
                    [728, 90]
                  ]
                },
                { viewport: [769, 0], sizes: [[300, 250]] },
                {
                  viewport: [0, 0],
                  sizes: [
                    [300, 250],
                    [320, 100],
                    [320, 50],
                    [300, 50]
                  ]
                }
              ]}
              targetingArguments={{ pos: '2' }}
            />
          </Box>
        </NoSsr>
      </AppLayout>
    </DFPSlotsProvider>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  try {
    const id = SlugModel.getIdFromSlug(context.params.slug, '-')
    const data = await EmpriseGroup.getBlogArticleById(id)

    return { props: { data } }
  } catch (e) {
    return { notFound: true }
  }
}

export default BlogCategory
